import type { FC } from 'react';
import type { GameMeta, GameType } from '@puzzlio/core';
import { GAME_META } from '@puzzlio/games';
import { FieldsLogo } from '@/app/components/fields-logo';
import { StarsLogo } from '@/app/components/stars-logo';
import { LoopyRiverLogo } from '@/app/components/loopy-river-logo';
import { MosaicLogo } from '@/app/components/mosaic-logo';

/**
 * How the web draws a game, on top of what the game *is*.
 *
 * The shared half lives in `@puzzlio/games` — labels, ids, routes, availability, colours a second
 * client would need. What's left here is the web's own rendering: Tailwind class names, `var()`
 * colour expressions, and React components, none of which mean anything off the web.
 */
export type GameConfig = GameMeta & {
  bgColor: string;
  lockBgColor: string;
  lockDotColor: string;
  lockShadowColor: string;
  separatorBorder: string;
  accent600: string;
  cardHeaderClassName: string;
  icon: FC<{ className?: string }>;
};

export const GAME_CONFIG: Record<GameType, GameConfig> = {
  stars: {
    ...GAME_META['stars'],
    bgColor: 'bg-yellow-100',
    lockBgColor: 'var(--yellow-200)',
    lockDotColor: 'bg-yellow-300',
    lockShadowColor: 'bg-yellow-500',
    separatorBorder: 'border-b-yellow-600 border-t-yellow-600',
    accent600: 'border-yellow-600',
    cardHeaderClassName: 'border-b-yellow-600 bg-yellow',
    icon: StarsLogo,
  },
  fields: {
    ...GAME_META['fields'],
    bgColor: 'bg-green-100',
    lockBgColor: 'var(--green-200)',
    lockDotColor: 'bg-green-300',
    lockShadowColor: 'bg-green-500',
    separatorBorder: 'border-b-green-600 border-t-green-600',
    accent600: 'border-green-600',
    cardHeaderClassName: 'border-b-green-600 bg-green-300',
    icon: FieldsLogo,
  },
  'loopy-river': {
    ...GAME_META['loopy-river'],
    // Loopy River's tutorial runs 'tutorial-0' then 'tutorial-1'; finishing the second one ends it.
    bgColor: 'bg-blue-100',
    lockBgColor: 'var(--blue-200)',
    lockDotColor: '#90EEFF',
    lockShadowColor: 'bg-purple-700',
    separatorBorder: 'border-b-blue-600 border-t-blue-600',
    accent600: 'border-blue-600',
    cardHeaderClassName: 'border-b-blue-600 bg-blue-300',
    icon: LoopyRiverLogo,
  },
  // ─── Mosaic ───────────────────────────────────────────────────────────────
  // Mosaic's identity colour is purple.
  mosaic: {
    ...GAME_META['mosaic'],
    // The whole Mosaic tutorial runs on this one board, so finishing it ends the tutorial.
    bgColor: 'bg-purple-100',
    // Purple-200 on purple-100 is a full token step apart, which reads heavier than the other games'
    // dots; so we tone it down to 60% alpha
    lockBgColor: 'var(--purple-200)',
    lockDotColor: 'var(--purple-300)',
    lockShadowColor: 'bg-purple-700',
    separatorBorder: 'border-b-purple-600 border-t-purple-600',
    accent600: 'border-purple-600',
    cardHeaderClassName: 'border-b-purple-600 bg-purple-300',
    icon: MosaicLogo,
  },
};

/** Game types that are publicly playable — everything in GAME_CONFIG except `comingSoon` games.
 *  Use this for user-facing surfaces (footer, learning center). */
/**
 * Whether a board is one of the tutorials. Broader than matching a game's `tutorialSlug`: several
 * games carry more than one tutorial board (tutorial, tutorial-0, tutorial-1), and all of them are
 * onboarding rather than a puzzle in the player's history.
 */
export function isTutorialBoard(slug: string | undefined): boolean {
  return !!slug && slug.startsWith('tutorial');
}

export const PLAYABLE_GAME_TYPES = (Object.keys(GAME_CONFIG) as GameType[]).filter(
  (g) => GAME_CONFIG[g].availability !== 'comingSoon'
);

/** Playable game types that participate in intro mode. Games without one are never put into intro
 *  mode, so their "Play" links go straight to the daily puzzle rather than to `tutorialURL`.
 *
 *  NOTE: this keys off `practiceCollections` as a proxy for "has the full onboarding treatment" —
 *  a game with practice puzzles also has a tutorial. The coupling is incidental: if you ever give a
 *  game practice puzzles *without* a tutorial route, move this to an explicit flag, or intro mode
 *  will link to a `tutorialURL` that 404s. */
export const INTRO_GAME_TYPES = PLAYABLE_GAME_TYPES.filter((g) => GAME_CONFIG[g].practiceCollections.length > 0);

/** Whether the given board slug is one of the game's practice-collection puzzles. */
export function isPracticeBoard(gameType: GameType, slug: string | null | undefined): boolean {
  if (!slug) return false;
  return GAME_CONFIG[gameType].practiceCollections.some((collection) => collection.puzzles.includes(slug));
}

/** URL of a practice puzzle board given its slug. */
export function practicePuzzleURL(gameType: GameType, slug: string): string {
  return `${GAME_CONFIG[gameType].url}/puzzle/${slug}`;
}
